import {
    PropRuntimeGraph,
    PropRuntimeNode,
    PropRuntimeNodeType,
} from '../../../../../shared/types/PropRuntimeGraph';
import {
    PropActionResult,
    PropNodeValue,
    RuntimePropInstance,
} from '../PropMechanicsTypes';
import {
    RuntimeActionNodeLike,
    RuntimeDataPortId,
    RuntimeNodeContext,
    RuntimeNodeLike,
} from './RuntimeNodeTypes';
import {
    isPropRuntimeGraph,
    isRuntimePropInstance,
    toBoolean,
    toNumber,
    uniqueLiveItems,
} from './RuntimeNodeUtils';

/**
 * 所有运行时节点的基类。
 *
 * 子类只需要实现自己的输出端口函数，例如 `value()`、`targets()`、`condition()`。
 * 输入端口通过本基类读取连接节点，并调用连接节点的默认输出端口函数。
 */
export abstract class RuntimeNodeBase implements RuntimeNodeLike {
    /** 当前运行时节点定义。 */
    public readonly node: PropRuntimeNode;
    /** 当前节点执行上下文。 */
    public readonly context: RuntimeNodeContext;

    /**
     * 创建节点实例。
     *
     * @param node 运行时图中的节点定义。
     * @param context 本次事件执行上下文。
     */
    public constructor(node: PropRuntimeNode, context: RuntimeNodeContext) {
        this.node = node;
        this.context = context;
    }

    /**
     * 调用节点数据输出端口。
     *
     * @param portId 被输入端口请求的数据输出端口。
     * @returns 端口输出值；端口不存在时返回 `undefined` 并记录警告。
     */
    public output(portId: RuntimeDataPortId): PropNodeValue {
        const method = (this as unknown as Record<string, unknown>)[portId];
        if (typeof method !== 'function') {
            this.context.warn(`节点 ${this.node.sourceId} 不存在输出端口 ${portId}`);
            return undefined;
        }
        return (method as () => PropNodeValue).call(this);
    }

    /** 读取输入端口连接的节点 id。 */
    protected inputNodeIds(portId: string): number[] {
        return this.context.readInputNodeIds(this.node, portId);
    }

    /** 读取输入端口连接节点的原始输出值。 */
    protected inputValues(portId: string): PropNodeValue[] {
        return this.inputNodeIds(portId)
            .map((nodeId) => this.context.createNode(nodeId))
            .filter((node): node is RuntimeNodeLike => Boolean(node))
            .map((node) => node.output(getDefaultOutputPort(node.node.type)));
    }

    /** 读取数字输入；未连接或无法转换时返回 fallback。 */
    protected inputNumber(portId: string, fallback = 0): number {
        const value = this.inputValues(portId)[0];
        return toNumber(value, fallback);
    }

    /** 读取布尔输入；多个条件输入按“全部成立”处理。 */
    protected inputCondition(portId: string, fallback = false): boolean {
        const values = this.inputValues(portId);
        if (!values.length) return fallback;
        return values.every((value) => toBoolean(value, fallback));
    }

    /** 读取目标数组输入；是否包含“空位置”由上游目标节点决定，本方法只做存活过滤和去重。 */
    protected inputTargets(portId: string, fallback: RuntimePropInstance[] = []): RuntimePropInstance[] {
        const result: RuntimePropInstance[] = [];
        for (const value of this.inputValues(portId)) {
            if (!Array.isArray(value)) continue;
            for (const item of value) {
                if (isRuntimePropInstance(item)) result.push(item);
            }
        }
        return uniqueLiveItems(result.length ? result : fallback, true);
    }

    /** 读取可作为筛选源的目标数组；元素可以是运行时实例，也可以是道具原型。 */
    protected inputTargetValues(portId: string): Array<RuntimePropInstance | PropRuntimeGraph> {
        const result: Array<RuntimePropInstance | PropRuntimeGraph> = [];
        for (const value of this.inputValues(portId)) {
            if (!Array.isArray(value)) continue;
            for (const item of value) {
                if (isRuntimePropInstance(item) || isPropRuntimeGraph(item)) result.push(item);
            }
        }
        return uniqueTargetValues(result);
    }

    /** 读取原型数组输入；未连接时返回空数组。 */
    protected inputGraphs(portId: string): PropRuntimeGraph[] {
        const result: PropRuntimeGraph[] = [];
        for (const value of this.inputValues(portId)) {
            if (!Array.isArray(value)) continue;
            for (const item of value) {
                if (isPropRuntimeGraph(item)) result.push(item);
                if (isRuntimePropInstance(item)) result.push(item.graph);
            }
        }
        return uniqueGraphs(result);
    }

    /** 读取原型 id 输入；兼容原型节点和目标节点输出。 */
    protected inputPrototypeIds(portId: string): string[] {
        return this.inputGraphs(portId).map((graph) => graph._id);
    }

    /** 读取当前动作目标；实例目标型动作只能使用 targets 输入，不允许在空结果时回退到自身。 */
    protected actionTargets(): RuntimePropInstance[] {
        return this.inputTargets('targets', []);
    }

    /** 在指定动作目标上下文中执行输入读取和状态变更。 */
    protected withActionTarget<T>(target: RuntimePropInstance, callback: () => T): T {
        return this.context.withCurrentTarget(target, callback);
    }

    /** 按当前目标上下文判断动作条件端口；未连接条件时默认成立。 */
    protected isActionConditionMatched(): boolean {
        return this.inputCondition('condition', true);
    }
}

/**
 * 动作节点基类。
 *
 * 动作节点负责修改运行时状态，并用 `PropActionResult` 告诉引擎后续应走成功或失败流程。
 */
export abstract class RuntimeActionNodeBase extends RuntimeNodeBase implements RuntimeActionNodeLike {
    /**
     * 读取动作节点的原型输入。
     *
     * @param portId 输入端口名称，常见为 `prototypes` 或兼容旧配置的 `targets`。
     * @returns 输入端口解析出的道具原型列表。
     */
    public readPrototypeInputGraphs(portId: string): PropRuntimeGraph[] {
        return this.inputGraphs(portId);
    }

    /**
     * 动作节点没有普通数据输出。
     *
     * @param portId 被请求的端口。
     * @returns 始终返回 undefined。
     */
    public output(portId: RuntimeDataPortId): PropNodeValue {
        this.context.warn(`动作节点 ${this.node.sourceId} 不支持数据输出端口 ${portId}`);
        return undefined;
    }

    /**
     * 执行动作节点。
     *
     * @returns 动作执行结果，决定 complete / fail / each 流程。
     */
    public abstract execute(): PropActionResult;
}

/** 判断节点实例是否为动作节点。 */
export function isRuntimeActionNode(node: RuntimeNodeLike): node is RuntimeActionNodeLike {
    return typeof (node as RuntimeActionNodeLike).execute === 'function';
}

function uniqueTargetValues(values: Array<RuntimePropInstance | PropRuntimeGraph>): Array<RuntimePropInstance | PropRuntimeGraph> {
    const seen = new Set<string>();
    const result: Array<RuntimePropInstance | PropRuntimeGraph> = [];
    for (const value of values) {
        if (isRuntimePropInstance(value) && !value.alive) continue;
        const key = isRuntimePropInstance(value) ? `instance:${value.id}` : `graph:${value._id}`;
        if (seen.has(key)) continue;
        seen.add(key);
        result.push(value);
    }
    return result;
}

/**
 * 获取节点默认输出端口。
 *
 * runtime graph 的输入连接只保存源节点 id，不保存源端口；因此运行时按节点类型推断默认端口。
 */
export function getDefaultOutputPort(type: PropRuntimeNodeType): RuntimeDataPortId {
    if (type === 'targetSelf') return 'target';
    if (type === 'targetBoard') return 'targets';
    if (type === 'prototypeLibrary' || type === 'prototypeSelect') return 'prototypes';
    if (type.indexOf('filter') === 0 || type === 'targetFilter') return 'targets';
    if (type.indexOf('logic') === 0) return 'condition';
    return 'value';
}

/** 创建标准动作成功结果。 */
export function actionSuccess(eachTargets: RuntimePropInstance[] = [], value?: PropNodeValue): PropActionResult {
    return { success: true, eachTargets, value };
}

/** 创建标准动作失败结果。 */
export function actionFail(value?: PropNodeValue): PropActionResult {
    return { success: false, eachTargets: [], value };
}

function uniqueGraphs(graphs: PropRuntimeGraph[]): PropRuntimeGraph[] {
    const seen = new Set<string>();
    const result: PropRuntimeGraph[] = [];
    for (const graph of graphs) {
        if (!graph || seen.has(graph._id)) continue;
        seen.add(graph._id);
        result.push(graph);
    }
    return result;
}
